import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuth } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/Login";
import PasswordResetPage from "@/pages/PasswordReset";
import DashboardPage from "@/pages/Dashboard";
import AuditLogPage from "@/pages/AuditLog";
import SettingsPage from "@/pages/Settings";
import AdminCompaniesPage from "@/pages/AdminCompanies";
import AdminUsersPage from "@/pages/AdminUsers";
import AdminPkiPage from "@/pages/AdminPki";
import AdminPecProvidersPage from "@/pages/AdminPecProviders";
import PecUploadPage from "@/pages/PecUpload";
import PecAnalysesPage from "@/pages/PecAnalyses";
import PecAnalysisPage from "@/pages/PecAnalysis";
import SendersPage from "@/pages/Senders";
import SuppliersPage from "@/pages/Suppliers";
import SupplierDetailPage from "@/pages/SupplierDetail";
import IbansPage from "@/pages/Ibans";
import RulesPage from "@/pages/Rules";
import ReviewsPage from "@/pages/Reviews";
import ReviewDetailPage from "@/pages/ReviewDetail";
import NotificationsPage from "@/pages/Notifications";
import ReportsPage from "@/pages/Reports";
import IntegrationsPage from "@/pages/Integrations";
import WebRiskConfigPage from "@/pages/WebRiskConfig";
import MailboxesPage from "@/pages/Mailboxes";
import PlaceholderPage from "@/pages/Placeholder";
import LandingPage from "@/pages/LandingPage";
import PecInboxPage from "@/pages/PecInbox";

/**
 * If already authenticated and visiting /, /login, or /password-reset,
 * bounce to the dashboard for better UX.
 */
function PublicOnlyRedirect({ children, redirectAuthenticated = false }) {
    const { user, loading } = useAuth();
    if (loading && !user) return children;
    if (user && redirectAuthenticated) return <Navigate to="/dashboard" replace />;
    return children;
}

function AppRoot() {
    const { refreshMe } = useAuth();
    useEffect(() => {
        refreshMe();
    }, [refreshMe]);

    return (
        <BrowserRouter>
            <Toaster
                position="top-right"
                richColors
                closeButton
                toastOptions={{
                    className: "!bg-white !text-[#172033] !border !border-[#DCE3EE]",
                }}
            />
            <Routes>
                <Route path="/" element={
                    <PublicOnlyRedirect redirectAuthenticated>
                        <LandingPage />
                    </PublicOnlyRedirect>
                } />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/password-reset" element={<PasswordResetPage />} />

                <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                    <Route path="/dashboard" element={<DashboardPage />} />

                    <Route path="/pec/inbox" element={<PecInboxPage />} />
                    <Route path="/pec/analyses" element={<PecAnalysesPage />} />
                    <Route path="/pec/analyses/:id" element={<PecAnalysisPage />} />
                    <Route path="/pec/upload" element={<PecUploadPage />} />
                    <Route path="/reviews" element={<ReviewsPage />} />
                    <Route path="/reviews/:id" element={<ReviewDetailPage />} />
                    <Route path="/senders" element={<SendersPage />} />
                    <Route path="/suppliers" element={<SuppliersPage />} />
                    <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
                    <Route path="/ibans" element={<IbansPage />} />
                    <Route path="/rules" element={<RulesPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/audit" element={<AuditLogPage />} />
                    <Route path="/integrations" element={<IntegrationsPage />} />
                    <Route path="/integrations/webrisk" element={<WebRiskConfigPage />} />
                    <Route path="/mailboxes" element={<MailboxesPage />} />
                    <Route path="/settings" element={<SettingsPage />} />

                    <Route path="/admin/companies" element={
                        <ProtectedRoute requireSuperAdmin>
                            <AdminCompaniesPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/users" element={
                        <ProtectedRoute requireAdmin>
                            <AdminUsersPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/pki" element={
                        <ProtectedRoute requireSuperAdmin>
                            <AdminPkiPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/pec-providers" element={
                        <ProtectedRoute requireSuperAdmin>
                            <AdminPecProvidersPage />
                        </ProtectedRoute>
                    } />

                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
}

function App() {
    return (
        <div className="App">
            <AppRoot />
        </div>
    );
}

export default App;
